import { useState, useCallback } from "react";
import {
  usePaginatedResource,
  PaginatedFetcher,
  pageFrom,
} from "./usePaginatedResource";
import { useEntityOperations } from "./useEntityOperations";
import { useEntity } from "./useEntity";
import { useEntitySearch, SearchPage } from "./useEntitySearch";
import { ExerciseService } from "../services/exerciseService";
import {
  Exercise,
  CreateExerciseInput,
  UpdateExerciseInput,
  ExerciseFilters,
} from "../types/exercise";

// Hook for managing exercises list with pagination and filtering
export const useExercises = (
  initialPage: number = 1,
  initialLimit: number = 15
) => {
  const fetcher = useCallback<PaginatedFetcher<Exercise, ExerciseFilters>>(
    async (page, filters) => {
      const response = await ExerciseService.getExercises(page, filters);
      return response ? pageFrom(response.exercises, response) : null;
    },
    []
  );

  const { items, ...rest } = usePaginatedResource<Exercise, ExerciseFilters>(
    fetcher,
    {
      initialPage,
      initialLimit,
      errorMessage: "Failed to fetch exercises",
    }
  );

  return { exercises: items, ...rest };
};

// Hook for managing individual exercise operations
export const useExerciseOperations = () => {
  const { run, runBoolean, ...rest } = useEntityOperations();

  const createExercise = useCallback(
    (exerciseData: CreateExerciseInput) =>
      run(
        () => ExerciseService.createExercise(exerciseData),
        "Failed to create exercise"
      ),
    [run]
  );

  const updateExercise = useCallback(
    (exerciseId: number, updates: UpdateExerciseInput) =>
      run(
        () => ExerciseService.updateExercise(exerciseId, updates),
        "Failed to update exercise"
      ),
    [run]
  );

  const deleteExercise = useCallback(
    (exerciseId: number) =>
      runBoolean(
        () => ExerciseService.deleteExercise(exerciseId),
        "Failed to delete exercise"
      ),
    [runBoolean]
  );

  return { createExercise, updateExercise, deleteExercise, ...rest };
};

// Hook for getting individual exercise data
export const useExercise = (exerciseId: number | null) => {
  const fetcher = useCallback(
    (id: number) => ExerciseService.getExerciseById(id),
    []
  );

  const { data, setData, ...rest } = useEntity<Exercise>(exerciseId, fetcher, {
    notFoundMessage: "Exercise not found",
    errorMessage: "An unexpected error occurred while fetching exercise",
  });

  return { exercise: data, setExercise: setData, ...rest };
};

// Hook for searching exercises with infinite scroll
export const useExerciseSearch = () => {
  const fetcher = useCallback(
    async (
      searchTerm: string,
      page: number
    ): Promise<SearchPage<Exercise> | null> => {
      const response = await ExerciseService.searchExercises(searchTerm, page);
      const paginator = response?.data?.data;
      if (!paginator) return null;

      return {
        items: paginator.data,
        total: paginator.total,
        page: paginator.current_page,
        lastPage: paginator.last_page,
      };
    },
    []
  );

  const { search, ...rest } = useEntitySearch<Exercise>(fetcher, {
    limit: 15,
    errorMessage: "Failed to search exercises",
  });

  return { search, searchExercises: search, ...rest };
};
